import { ArrowRight, Mail } from "lucide-react";
import Reveal from "@/components/Reveal";
import { FOUNDER, CALENDLY_URL, CONTACT_EMAIL } from "@/content";

export default function FounderSection() {
  return (
    <section className="border-t border-white/10 py-20 lg:py-28" data-testid="about-founder">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-[380px_1fr] lg:gap-20">
          <Reveal>
            <figure className="max-w-sm lg:max-w-none" data-testid="founder-headshot">
              <div className="relative">
                <img
                  src={FOUNDER.photo}
                  alt={FOUNDER.alt}
                  className="aspect-square w-full border border-white/10 object-cover object-top"
                  loading="eager"
                />
                <span className="absolute -right-px -top-px h-7 w-7 border-r-[6px] border-t-[6px] border-gold" aria-hidden="true" />
              </div>
              <figcaption className="mt-4 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500" data-testid="founder-caption">
                {FOUNDER.caption}
              </figcaption>
            </figure>
          </Reveal>

          <Reveal delay={0.1}>
            <p className="eyebrow mb-6">{FOUNDER.eyebrow}</p>
            <h2 className="font-display max-w-2xl text-2xl sm:text-3xl lg:text-4xl font-bold tracking-[-0.03em] leading-tight text-white">
              {FOUNDER.headline}
            </h2>
            <div className="mt-8 max-w-2xl space-y-5">
              {FOUNDER.intro.map((p, i) => (
                <p
                  key={i}
                  className={`${i === 0 ? "text-lg text-slate-200" : "prose-justify text-base text-slate-400"} leading-relaxed`}
                >
                  {p}
                </p>
              ))}
            </div>
          </Reveal>
        </div>

        <div className="mt-20 grid gap-10 border-t border-white/10 pt-16 lg:grid-cols-[260px_1fr] lg:gap-20" data-testid="founder-why">
          <Reveal>
            <p className="eyebrow-blue">{FOUNDER.why.title}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="max-w-3xl">
              {FOUNDER.why.body.map((p, i) => (
                <p key={i} className={`${i === 0 ? "font-display text-xl sm:text-2xl text-white tracking-[-0.02em]" : "prose-justify mt-5 text-base text-slate-400"} leading-relaxed`}>
                  {p}
                </p>
              ))}
              <p className="mt-8 text-sm font-medium text-slate-300">{FOUNDER.why.lead}</p>
              <ul className="mt-5 grid gap-x-10 gap-y-3 sm:grid-cols-2">
                {FOUNDER.why.items.map((it, i) => (
                  <li key={it} className="flex gap-4 text-sm text-slate-400 leading-relaxed" data-testid={`founder-why-item-${i + 1}`}>
                    <span className="font-mono text-xs text-gold/70">0{i + 1}</span>
                    {it}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>

        <div className="mt-16 border-t border-white/10">
          {FOUNDER.sections.map((s, i) => (
            <Reveal key={s.title}>
              <div className="grid gap-4 border-b border-white/10 py-10 lg:grid-cols-[260px_1fr] lg:gap-20" data-testid={`founder-section-${i + 1}`}>
                <h3 className="font-display text-xl font-bold tracking-[-0.03em] leading-snug text-white">{s.title}</h3>
                <div className="max-w-3xl space-y-4">
                  {s.body.map((p, j) => (
                    <p key={j} className="prose-justify text-base text-slate-400 leading-relaxed">{p}</p>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-14 flex flex-col items-start gap-5" data-testid="founder-cta">
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="founder-book-conversation-button"
              className="group inline-flex items-center gap-2 bg-gold px-7 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-navy transition-all duration-300 hover:bg-gold-glow"
            >
              {FOUNDER.cta}
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              data-testid="founder-email-link"
              className="inline-flex items-center gap-2.5 text-sm text-slate-300 transition-colors hover:text-gold"
            >
              <Mail size={14} className="text-gold" />
              {CONTACT_EMAIL}
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
